import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import { injectIntl } from '../../util/reactIntl';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import { loadData } from './SignupProviderPage.duck';
import {
  SignupProviderIntroStep,
  SignupProviderInfoStep,
  SignupProviderBioForm,
  SignupProviderEducationForm,
  SignupProviderHourlyFeeForm,
  SignupProviderLocationNLanguageForm,
  SignupProviderSkillsForm,
  SignupProviderToolsForm,
  SignupProviderWhoAmIFrom,
  SignupProviderWorkExperienceInfoStep,
  SignupProviderWorkExperienceForm,
  SignupProviderWorkedWithForm,
  SignupProviderLinksForm,
  SignupProviderCOC,
  SignupProviderHobbiesForm,
  SignupProviderIndexForm,
} from '../../forms';

import {
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
  ProgressBar,
  Modal,
  TopbarBlank,
} from '../../components';
import { AuthenticationPage } from '../../containers';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import config from '../../config';

import css from './SignupProviderPage.css';

const renderSignUpStep = (step, next, prev, formData, nextFromInfo, skillOptions, toolOptions) => {
  switch (step) {
    case 0:
      return <SignupProviderIntroStep onSubmit={nextFromInfo} initialValues={formData} />;
    case 1:
      return (
        <SignupProviderInfoStep onSubmit={nextFromInfo} onPrev={prev} initialValues={formData} />
      );
    case 2:
      return <SignupProviderWhoAmIFrom onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 3:
      return (
        <SignupProviderSkillsForm
          onSubmit={values => {
            const { experience } = values;
            const newExperience = experience.map(item =>
              item.isMainSkill ? item : { ...item, isMainSkill: false }
            );
            next({ experience: newExperience });
          }}
          onPrev={prev}
          initialValues={formData}
          skillOptions={skillOptions}
        />
      );
    case 4:
      return (
        <SignupProviderToolsForm
          onSubmit={next}
          onPrev={prev}
          initialValues={formData}
          toolOptions={toolOptions}
        />
      );
    case 5:
      return <SignupProviderHourlyFeeForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 6:
      return (
        <SignupProviderLocationNLanguageForm
          onSubmit={next}
          onPrev={prev}
          initialValues={formData}
        />
      );
    case 7:
      return <SignupProviderLinksForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 8:
      return <SignupProviderEducationForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 9:
      return (
        <SignupProviderWorkExperienceInfoStep
          onSubmit={nextFromInfo}
          onPrev={prev}
          initialValues={formData}
        />
      );
    case 10:
      return (
        <SignupProviderWorkExperienceForm
          onSubmit={values => {
            const { workExperience, dontAddWorkExperience } = values;
            const newWorkExperience = workExperience
              ? workExperience.map(item =>
                  item.jobContinues ? { ...item, endMonth: undefined, endYear: undefined } : item
                )
              : [];

            next({ workExperience: newWorkExperience, dontAddWorkExperience });
          }}
          onPrev={prev}
          initialValues={formData}
        />
      );
    case 11:
      return (
        <SignupProviderWorkedWithForm
          onSubmit={values => {
            const { workedWith, dontAddWorkedWith } = values;
            const newWorkedWith = workedWith
              ? workedWith.map(item =>
                  item.jobContinues ? { ...item, endMonth: undefined, endYear: undefined } : item
                )
              : [];

            next({ workedWith: newWorkedWith, dontAddWorkedWith });
          }}
          onPrev={prev}
          initialValues={formData}
        />
      );
    case 12:
      return <SignupProviderHobbiesForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 13:
      return <SignupProviderBioForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 14:
      return <SignupProviderIndexForm onSubmit={next} onPrev={prev} initialValues={formData} />;
    case 15:
      return (
        <SignupProviderCOC
          onSubmit={values => {
            const { cocAccepted, fnameProvider, lnameProvider } = values;
            next({ cocAccepted, fnameProvider, lnameProvider });
          }}
          onPrev={prev}
          initialValues={formData}
        />
      );
    default:
      return null;
  }
};

const initialFormData = {
  // Array with one empty object for FieldArrays initially to have one set of fields visible on first render.
  experience: [{}],
  tools: [{}],
  languages: [{}],
  education: [{}],
  workExperience: [{}],
  workedWith: [{}],
  hobbies: [{}],
};

const getCurrentStep = () => {
  if (typeof window !== 'undefined' && !!window.sessionStorage) {
    return parseInt(sessionStorage.getItem('providerSignupStep'), 10) || 0;
  }
  return 0;
};

const SignupProviderPageComponent = props => {
  const [formData, setFormData] = useState(initialFormData);
  const [step, setStep] = useState(getCurrentStep());
  const { onManageDisableScrolling, scrollingDisabled, intl, skillOptions, toolOptions } = props;
  const [isAuthOpen, setIsAuthOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      setFormData(getInitialFormData());
    }
  }, [step]);

  const getInitialFormData = () => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      return JSON.parse(sessionStorage.getItem('providerFormData')) || initialFormData;
    }
    return initialFormData;
  };

  const nextFromInfo = () => {
    setStep(prevStep => prevStep + 1);
  };

  const setNextStepToStorage = prevStep => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      sessionStorage.setItem('providerSignupStep', prevStep + 1);
      setStep(prevStep + 1);
    }
  };

  const setPreviousStepToStorage = prevStep => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      sessionStorage.setItem('providerSignupStep', prevStep - 1);
      setStep(prevStep - 1);
    }
  };

  const next = data => {
    const newFormData = {
      ...formData,
      ...data,
    };
    sessionStorage.setItem('providerFormData', JSON.stringify(newFormData));
    setFormData(newFormData);
    return step === 15 ? setUpLastStep(newFormData) : setNextStepToStorage(step);
  };

  const prev = e => {
    e.preventDefault();
    setStep(prevStep => prevStep - 1);
    setPreviousStepToStorage(step);
  };

  const setUpLastStep = data => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      sessionStorage.setItem('providerFormData', JSON.stringify(data));
    }
    setIsAuthOpen(true);
  };

  const completedValue = ((step / 15) * 100).toFixed(2);

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage(
    { id: 'AuthenticationPage.schemaTitleSignup' },
    { siteTitle }
  );

  const showUnsavedPrompt = !isEqual(formData, initialFormData);

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarBlank />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.main}>
            {renderSignUpStep(step, next, prev, formData, nextFromInfo, skillOptions, toolOptions)}
            <ProgressBar value={completedValue} stepsDone={step} steps={13} hideStepCount />
            {/* <div className={css.nextUpContainer}>
              <div className={css.nextUpLine}>
                <span className={css.nextUpText}>
                  <FormattedMessage id={getNextUpTranslation()} />
                </span>
              </div>
            </div> */}
            <Modal
              id="SignupProviderAuthModal"
              containerClassName={css.modal}
              isOpen={isAuthOpen}
              onClose={() => setIsAuthOpen(false)}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <AuthenticationPage tab="signup" showUnsavedPrompt={showUnsavedPrompt} />
            </Modal>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

SignupProviderPageComponent.defaultProps = {};

SignupProviderPageComponent.propTypes = {};

const mapStateToProps = state => {
  const { skillOptions, toolOptions } = state.SkillsAndTools;

  return {
    skillOptions,
    toolOptions,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SignupProviderPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(SignupProviderPageComponent);

SignupProviderPage.loadData = loadData;

export default SignupProviderPage;
